/* eslint-disable no-unused-vars */

// import {
//   FormControl,
//   FormHelperText,
//   InputLabel,
//   MenuItem,
//   Select
// } from '@material-ui/core';
// import React from 'react';
// import { purchaseOrderStatusOptions } from 'src/redux/slices/purchase-order';
// import { PurchaseOrderStatusOption } from 'src/redux/slices/purchase-order/types';

// type Props = {
//   label?: string;
//   value?: string;
//   onChange: (val: PurchaseOrderStatusOption) => void;
//   error?: boolean;
//   helperText?: string;
//   fullWidth?: boolean;
//   disabled?: boolean;
// };

// const PurchaseOrderStatusSelect: React.FC<Props> = ({
//   label = 'Purchase Order Status',
//   value,
//   onChange,
//   error = false,
//   helperText,
//   fullWidth = true,
//   disabled = false
// }) => {
//   return (
//     <FormControl variant='outlined' fullWidth={fullWidth} error={error} disabled={disabled}>
//       <InputLabel>{label}</InputLabel>
//       <Select
//         value={value}
//         label={label}
//         onChange={(e: any) => onChange(e.target.value)}
//       >
//         {purchaseOrderStatusOptions?.map((option) => (
//           <MenuItem key={option.value} value={option.value}>
//             {option.label}
//           </MenuItem>
//         ))}
//       </Select>
//       {helperText && <FormHelperText>{helperText}</FormHelperText>}
//     </FormControl>
//   );
// };

// export default PurchaseOrderStatusSelect;

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TextField,
  FormHelperText,
  FormControl,
  Typography
} from '@material-ui/core'; // or '@mui/material' if using MUI v5+
import {
  allowedTransitions,
  PurchaseOrderStatusesV2,
  purchaseOrderStatusOptions,
  purchaseOrderStatusOptionsV2,
  roleBasedTransitions,
  statusOptionsPerRole
} from 'src/redux/slices/purchase-order';
import { PurchaseOrderStatusOption } from 'src/redux/slices/purchase-order/types';
import { Autocomplete } from '@material-ui/lab';
import useUserInfo from 'src/hooks/user/use-user-info';

type Props = {
  id?: string;
  label?: string;
  isFilter?: boolean;
  value?: string | null;
  currentValue?: string | null;
  onChange: (val: PurchaseOrderStatusOption | null) => void;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  isDetailsPage?: boolean;
  onBlur?: (event: any) => void;
  size?: 'small' | 'medium';
};

const PurchaseOrderStatusField: React.FC<Props> = ({
  label = '',
  value,
  isFilter = false,
  onChange,
  error = false,
  currentValue = '',
  helperText,
  fullWidth = true,
  disabled = false,
  isDetailsPage = false,
  onBlur,
  id = 'status',
  size = 'medium'
}) => {
  const { userDetails } = useUserInfo();

  const [inputValue, setInputValue] = useState<string>('');

  const isOwner = useMemo(() => {
    return userDetails?.all_roles?.some((role) => role.name === 'owner');
  }, [userDetails]);

  const canViewAllStatus = useMemo(() =>{
    return userDetails?.all_permissions?.some((permission)=> permission.name === 'can_view_all_po_status')
  }, [userDetails])

  const canUserTransition = (
    from: string,
    to: string,
    userRelevantRoles: Set<string>
  ) => {
    if (from === PurchaseOrderStatusesV2.CANCEL) {
      return true;
    }

    return roleBasedTransitions.some(
      (t) =>
        t.from === from &&
        t.to === to &&
        t.roles.some((role) => userRelevantRoles.has(role))
    );
  };

  const selectedStatus = useMemo(
    () =>
      purchaseOrderStatusOptionsV2?.find((option) => option.value === value),
    [value]
  );

  const conditionalOptions = useMemo(() => {
    if (isOwner && !isFilter) {
      return purchaseOrderStatusOptionsV2
        .filter(
          (option) =>
            option.value !== PurchaseOrderStatusesV2.CANCEL ||
            currentValue === PurchaseOrderStatusesV2.CANCEL
        )
        .map((option) => ({
          ...option,
          isDisabled: false
        }));
    } else {
      if (isOwner || canViewAllStatus) {
        return purchaseOrderStatusOptionsV2.map((option) => ({
          ...option,
          isDisabled: false
        }));
      }
    }

    const relevantStatuses = new Set<string>();
    const relevantRoles = new Set<string>();

    userDetails?.all_roles?.forEach(({ name }) => {
      if (name && statusOptionsPerRole[name]) {
        statusOptionsPerRole[name].forEach((status) => {
          relevantStatuses.add(status);
          relevantRoles.add(name);
        });
      }
    });

    if (isDetailsPage) {
      const currentStatus = currentValue || '';
      const allStatuses = purchaseOrderStatusOptionsV2.map(
        (option) => option.value
      );

      const currentIndex = allStatuses.indexOf(currentStatus);

      if (currentIndex === -1) {
        console.error('Invalid current status');
        return purchaseOrderStatusOptionsV2.filter((option) =>
          relevantStatuses.has(option.value)
        );
      }

      const nextAllowed =
        allowedTransitions?.[currentStatus]?.filter(
          (status) =>
            relevantStatuses.has(status) &&
            allStatuses.indexOf(status) > currentIndex &&
            canUserTransition(currentStatus, status, relevantRoles)
        ) || [];

      const allowed = new Set<string>([
        ...allStatuses.slice(0, currentIndex + 1),
        ...nextAllowed
      ]);

      return purchaseOrderStatusOptionsV2
        .filter((option) => allowed.has(option.value))
        .map((option) => ({
          ...option,
          isDisabled:
            option.value === currentStatus ||
            !nextAllowed.includes(option.value)
        }));
    }

    // fallback non-details
    return purchaseOrderStatusOptionsV2.filter((option) =>
      relevantStatuses.has(option.value)
    );
  }, [isOwner, isFilter, userDetails, isDetailsPage, currentValue]);

  const isPassedStatus = useCallback(
    (status: string) => {
      //bypass status skip logic for owner
      if (isOwner) {
        return false;
      }

      if (currentValue === PurchaseOrderStatusesV2.CANCEL) {
        return false;
      }

      const statusIndex = conditionalOptions.findIndex(
        ({ value }) => value === status
      );

      const currentValIndex = conditionalOptions.findIndex(
        ({ value }) => value === currentValue
      );

      const isSpecialist = userDetails?.all_roles?.some(
        (role) => role?.name === 'Purchasing Product Specialist II'
      );

      const specialCases = [
        currentValue === PurchaseOrderStatusesV2.FOR_APPROVAL &&
          status === PurchaseOrderStatusesV2.DRAFT,

        currentValue === PurchaseOrderStatusesV2.DECLINED &&
          status === PurchaseOrderStatusesV2.FOR_APPROVAL,

        isSpecialist &&
          currentValue === PurchaseOrderStatusesV2.APPROVED &&
          status === PurchaseOrderStatusesV2.FOR_APPROVAL
      ];

      return specialCases.some(Boolean) ? false : statusIndex < currentValIndex;
    },
    [conditionalOptions, currentValue, isOwner, userDetails]
  );

  const onLocalBlur = (e: any) => {
    if (!isDetailsPage) {
      const matched = conditionalOptions.find(
        (opt) => opt.label.toLowerCase() === inputValue.toLowerCase()
      );
      if (!matched) {
        setInputValue(selectedStatus?.label || '');
      }
    }
    onBlur?.(e);
  };

  return (
    <FormControl fullWidth={fullWidth} error={error} disabled={disabled}>
      <Autocomplete
        id={id}
        style={{ minWidth: 205, color: selectedStatus?.color }}
        size={size}
        options={conditionalOptions}
        onBlur={onLocalBlur}
        getOptionLabel={(option) => option?.label}
        value={selectedStatus || null}
        inputValue={inputValue} // display value
        onChange={(_, newValue) => {
          if (isDetailsPage && isPassedStatus(newValue?.value || '')) return;
          onChange(newValue);
          setInputValue(newValue?.label || '');
        }}
        onInputChange={(_, newValue) => {
          setInputValue(newValue);
        }}
        // isOptionEqualToValue={(option, val) => option?.value === val?.value}
        disabled={disabled} //todo: disabled based on role
        renderOption={(option) => (
          <Typography
            style={{
              color:
                isDetailsPage && isPassedStatus(option.value)
                  ? 'grey'
                  : option?.color || undefined
            }}
          >
            {option.label}
          </Typography>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            error={error}
            helperText={helperText}
            style={{
              borderRadius: '4px'
            }}
            inputProps={{
              ...params.inputProps,
              style: { color: selectedStatus?.color }
            }}
          />
        )}
      />
      {error && helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default PurchaseOrderStatusField;
